/**
 * HTML Analyzer for extracting pricing table structure
 * Automatically detects tiers and billing periods from HTML content
 * 
 * ⚠️ DEVELOPMENT/TESTING ONLY ⚠️
 * This autofill functionality is intended for development and testing purposes only.
 * It should NEVER be used in production or by end users.
 * Production users must manually configure their pricing tables.
 */

export interface ExtractedPricingData {
  tiers: number;
  billingPeriods: string[];
  tierNames: string[];
  detectedPrices: {
    [tierIndex: number]: {
      [period: string]: {
        price: string;
        link?: string;
      };
    };
  };
}

export class HTMLPricingAnalyzer {
  private html: string;

  constructor(html: string) {
    this.html = html;
    
    // Safety check - warn if this is being used outside of test mode
    if (typeof window !== 'undefined' && !window.location.href.includes('localhost') && !window.location.href.includes('127.0.0.1')) {
      console.warn('⚠️ WARNING: HTMLPricingAnalyzer is being used in what appears to be a production environment. This should only be used for development/testing.');
    }
  }

  /**
   * Extract pricing table structure from HTML
   */
  public extractPricingData(): ExtractedPricingData {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.html, 'text/html');
    
    // Extract tier information
    const tierInfo = this.extractTierInfo(doc);
    
    // Extract billing period information
    const billingPeriods = this.extractBillingPeriods(doc);
    
    // Extract prices for each tier and billing period
    const detectedPrices = this.extractPrices(doc, tierInfo.tierNames, billingPeriods);
    
    return {
      tiers: tierInfo.tierNames.length,
      billingPeriods,
      tierNames: tierInfo.tierNames,
      detectedPrices
    };
  }

  /**
   * Extract tier names and structure from HTML
   */
  private extractTierInfo(doc: Document): { tierNames: string[] } {
    const tierNames: string[] = [];
    
    // Look for common tier name patterns
    const tierSelectors = [
      '.tier-name',
      '.plan-name',
      '.pricing-tier h3',
      '.pricing-plan h3',
      '.tier-header h3',
      '.plan-header h3',
      '[class*="tier"] h3',
      '[class*="plan"] h3'
    ];
    
    for (const selector of tierSelectors) {
      const elements = doc.querySelectorAll(selector);
      if (elements.length > 0) {
        elements.forEach(element => {
          const tierName = element.textContent?.trim();
          if (tierName && !tierNames.includes(tierName)) {
            tierNames.push(tierName);
          }
        });
        break; // Use first successful selector
      }
    }
    
    // Fallback: look for tier containers
    if (tierNames.length === 0) {
      const tierContainers = doc.querySelectorAll('.pricing-tier, .pricing-plan, [class*="tier"], [class*="plan"]');
      tierContainers.forEach((container, index) => {
        const name = this.extractTierNameFromContainer(container);
        if (name) {
          tierNames.push(name);
        } else {
          tierNames.push(`Tier ${index + 1}`);
        }
      });
    }
    
    return { tierNames };
  }

  /**
   * Extract tier name from a container element
   */
  private extractTierNameFromContainer(container: Element): string | null {
    // Look for h1, h2, h3, h4, h5, h6 elements
    const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
    for (const heading of headings) {
      const text = heading.textContent?.trim();
      if (text && text.length > 0 && text.length < 50) {
        return text;
      }
    }
    
    // Look for elements with tier/plan related classes
    const tierElements = container.querySelectorAll('[class*="tier"], [class*="plan"], [class*="name"]');
    for (const element of tierElements) {
      const text = element.textContent?.trim();
      if (text && text.length > 0 && text.length < 50) {
        return text;
      }
    }
    
    return null;
  }

  /**
   * Extract billing periods from HTML
   */
  private extractBillingPeriods(doc: Document): string[] {
    const billingPeriods: string[] = [];
    
    // Look for billing toggle elements
    const toggleElements = doc.querySelectorAll('.billing-toggle, .period-toggle, [class*="toggle"]');
    for (const element of toggleElements) {
      const text = element.textContent?.toLowerCase();
      if (text) {
        if (text.includes('monthly') && !billingPeriods.includes('monthly')) {
          billingPeriods.push('monthly');
        }
        if (text.includes('quarterly') && !billingPeriods.includes('quarterly')) {
          billingPeriods.push('quarterly');
        }
        if (text.includes('annual') && !billingPeriods.includes('annually')) {
          billingPeriods.push('annually');
        }
        if (text.includes('yearly') && !billingPeriods.includes('annually')) {
          billingPeriods.push('annually');
        }
      }
    }
    
    // Look for price elements with period indicators
    const priceElements = doc.querySelectorAll('.price, [class*="price"]');
    for (const element of priceElements) {
      const text = element.textContent?.toLowerCase();
      if (text) {
        if (text.includes('/month') && !billingPeriods.includes('monthly')) {
          billingPeriods.push('monthly');
        }
        if (text.includes('/quarter') && !billingPeriods.includes('quarterly')) {
          billingPeriods.push('quarterly');
        }
        if (text.includes('/year') && !billingPeriods.includes('annually')) {
          billingPeriods.push('annually');
        }
        if (text.includes('/annual') && !billingPeriods.includes('annually')) {
          billingPeriods.push('annually');
        }
      }
    }
    
    // Look for hidden price elements (like in table2_basic)
    const hiddenElements = doc.querySelectorAll('.hidden, [style*="display: none"], [style*="display:none"]');
    for (const element of hiddenElements) {
      const text = element.textContent?.toLowerCase();
      if (text) {
        if (text.includes('quarterly') && !billingPeriods.includes('quarterly')) {
          billingPeriods.push('quarterly');
        }
        if (text.includes('annual') && !billingPeriods.includes('annually')) {
          billingPeriods.push('annually');
        }
      }
    }
    
    // Default fallback
    if (billingPeriods.length === 0) {
      billingPeriods.push('monthly', 'annually');
    }
    
    return billingPeriods;
  }

  /**
   * Extract prices for each tier and billing period
   */
  private extractPrices(doc: Document, tierNames: string[], billingPeriods: string[]): ExtractedPricingData['detectedPrices'] {
    const detectedPrices: ExtractedPricingData['detectedPrices'] = {};
    
    // Initialize structure
    tierNames.forEach((_, tierIndex) => {
      detectedPrices[tierIndex] = {};
      billingPeriods.forEach(period => {
        detectedPrices[tierIndex][period] = { price: '' };
      });
    });
    
    // Look for price elements
    const priceElements = doc.querySelectorAll('.price, [class*="price"], .amount, [class*="amount"]');
    
    priceElements.forEach(element => {
      const text = element.textContent?.trim();
      if (text) {
        const price = this.extractPriceFromText(text);
        if (price) {
          // Try to determine which tier and period this price belongs to
          const tierIndex = this.findTierIndexForElement(element, tierNames);
          const period = this.findBillingPeriodForElement(element, billingPeriods);
          
          if (tierIndex !== -1 && period) {
            detectedPrices[tierIndex][period] = { 
              price,
              link: this.extractLinkFromElement(element)
            };
          }
        }
      }
    });
    
    return detectedPrices;
  }

  /**
   * Extract price from text (remove currency symbols, keep numbers)
   */
  private extractPriceFromText(text: string): string | null {
    // Remove currency symbols and extract numbers
    const priceMatch = text.match(/[\d,]+\.?\d*/);
    if (priceMatch) {
      return priceMatch[0].replace(/,/g, '');
    }
    return null;
  }

  /**
   * Find which tier an element belongs to
   */
  private findTierIndexForElement(element: Element, tierNames: string[]): number {
    // Walk up the DOM tree to find the tier container
    let current = element;
    while (current && current !== current.ownerDocument?.body) {
      // Check if this element or its parent contains tier information
      const text = current.textContent?.toLowerCase();
      if (text) {
        for (let i = 0; i < tierNames.length; i++) {
          if (text.includes(tierNames[i].toLowerCase())) {
            return i;
          }
        }
      }
      
      // Check for tier-related classes
      const className = current.className?.toLowerCase() || '';
      if (className.includes('tier') || className.includes('plan')) {
        // Try to extract tier number from class name
        const tierMatch = className.match(/tier-?(\d+)|plan-?(\d+)/);
        if (tierMatch) {
          const tierNum = parseInt(tierMatch[1] || tierMatch[2]);
          return Math.min(tierNum - 1, tierNames.length - 1);
        }
      }
      
      current = current.parentElement as Element;
    }
    
    // Fallback: return 0 for first tier
    return 0;
  }

  /**
   * Find which billing period an element belongs to
   */
  private findBillingPeriodForElement(element: Element, billingPeriods: string[]): string | null {
    // Check element's class names
    const className = element.className?.toLowerCase() || '';
    for (const period of billingPeriods) {
      if (className.includes(period)) {
        return period;
      }
    }
    
    // Check element's text content
    const text = element.textContent?.toLowerCase() || '';
    for (const period of billingPeriods) {
      if (text.includes(period)) {
        return period;
      }
    }
    
    // Walk up the DOM tree
    let current = element;
    while (current && current !== current.ownerDocument?.body) {
      const currentText = current.textContent?.toLowerCase() || '';
      const currentClass = current.className?.toLowerCase() || '';
      
      for (const period of billingPeriods) {
        if (currentText.includes(period) || currentClass.includes(period)) {
          return period;
        }
      }
      
      current = current.parentElement as Element;
    }
    
    // Default to first billing period
    return billingPeriods[0] || null;
  }

  /**
   * Extract link from element or nearby elements
   */
  private extractLinkFromElement(element: Element): string | undefined {
    // Check if element is a link
    if (element.tagName === 'A') {
      return (element as HTMLAnchorElement).href;
    }
    
    // Look for nearby link elements
    const link = element.querySelector('a');
    if (link) {
      return (link as HTMLAnchorElement).href;
    }
    
    // Check parent elements for links
    let current = element.parentElement;
    while (current && current !== current.ownerDocument?.body) {
      if (current.tagName === 'A') {
        return (current as HTMLAnchorElement).href;
      }
      current = current.parentElement;
    }
    
    return undefined;
  }
}

/**
 * Utility function to analyze HTML and extract pricing data
 * 
 * ⚠️ DEVELOPMENT/TESTING ONLY ⚠️
 * This function should only be used in test mode for development purposes.
 * Production users must manually configure their pricing tables.
 */
export function analyzeHTMLForPricing(html: string): ExtractedPricingData {
  const analyzer = new HTMLPricingAnalyzer(html);
  return analyzer.extractPricingData();
}
